import moment, { Moment, MomentInput } from 'moment';
import { IntlShape } from 'react-intl';
import {
  AssignedProject,
  BulkOrderError,
  BulkOrderRenewRequest,
  BulkOrderRow,
  BulkOrderRowValues,
  OrderCreateRequest,
  OrderDetails,
  OrderEvent,
  OrderLifecycleStatus,
  OrderRenewRequest,
  OrderSummary,
  OrderViewStatus,
  SelectOption,
} from './types';

// A date field whose input holds no usable date (DateField): the prefix,
// followed by the typed text so the field can show it again after a remount.
export const INVALID_DATE = 'invalid:';
export const isInvalidDate = (value?: string) =>
  Boolean(value?.startsWith(INVALID_DATE));

// Status groups of the work order lists (Waiting, Active, Future, History,
// All). My work orders and All work orders use the same view statuses per
// group, so their tab contents and counts match for one consultant.
// The groups follow the view status, not the lifecycle: an assignment ended
// early stays ACTIVE (so under Active) through its effective end date and
// becomes EARLY_ENDED (History) the day after; the backend list filter
// applies the same rule (change request of 15.10, item 1).
export type OrderGroup = 'all' | 'pending' | 'future' | 'active' | 'history';

// Tab order of both lists.
export const ORDER_GROUPS: OrderGroup[] = [
  'pending',
  'active',
  'future',
  'history',
  'all',
];

export const ORDER_GROUP_STATUSES: Record<
  OrderGroup,
  OrderViewStatus[] | undefined
> = {
  all: undefined,
  pending: ['PENDING'],
  future: ['FUTURE'],
  active: ['ACTIVE'],
  history: ['COMPLETED', 'REJECTED', 'EXPIRED', 'CANCELLED', 'EARLY_ENDED'],
};

export const isOrderGroup = (value: string | null): value is OrderGroup =>
  Object.keys(ORDER_GROUP_STATUSES).includes(value as string);

// Both lists open on Active; My assignments opens on Waiting while something
// waits for the consultant (owner, 24.10, D8).
export const DEFAULT_ORDER_GROUP: OrderGroup = 'active';

// Order numbers are ORD-{prefix}{counter}: the optional prefix is 1-6
// letters or digits chosen by the issuer and the backend appends the (at
// least 4-digit) counter; without a prefix the number is ORD-{counter}
// (owner, 24.10, D7).
export const ORDER_CODE_PATTERN = /^[A-Z0-9]{1,6}$/;
const ORDER_NUMBER_PREFIX = 'ORD-';
const MIN_COUNTER_DIGITS = 4;

export const normalizeOrderCode = (value?: string | null) =>
  (value || '').trim().toUpperCase();

export const validateOrderCode = (value?: string | null) => {
  const code = normalizeOrderCode(value);
  return !code || ORDER_CODE_PATTERN.test(code)
    ? ''
    : 'ORDERS.VALIDATION.ORDER_CODE';
};

// The prefix for a request: null when blank, so the backend numbers the
// assignment ORD-{counter}.
export const toOrderCode = (value?: string | null) =>
  normalizeOrderCode(value) || null;

// Recovers the code from a new-format number (ORD-LPPRFD0007 -> LPPRFD).
// Legacy numbers such as ORD-00000001 carry no code and return ''. The
// longest valid code wins, so digits inside a code are kept when possible.
export const getOrderCodeFromNumber = (orderNumber?: string | null) => {
  if (!orderNumber?.startsWith(ORDER_NUMBER_PREFIX)) {
    return '';
  }
  const rest = orderNumber.slice(ORDER_NUMBER_PREFIX.length).toUpperCase();
  for (
    let counterLength = MIN_COUNTER_DIGITS;
    counterLength < rest.length;
    counterLength += 1
  ) {
    const counter = rest.slice(rest.length - counterLength);
    const code = rest.slice(0, rest.length - counterLength);
    if (!/^\d+$/.test(counter)) {
      return '';
    }
    if (ORDER_CODE_PATTERN.test(code) && /[A-Z]/.test(code)) {
      return code;
    }
  }
  return '';
};

// The prefix an extension of all the given assignments keeps: theirs when
// they share one, else none.
export const getSharedOrderCode = (orders: Array<{ orderNumber: string }>) => {
  const codes = Array.from(
    new Set(
      orders.map(({ orderNumber }) => getOrderCodeFromNumber(orderNumber)),
    ),
  );
  return codes.length === 1 ? codes[0] : '';
};

export const validateReportPeriod = (from: string, to: string) => {
  if (!from || !to) {
    return 'ORDER_OVERSIGHT.VALIDATION.REQUIRED';
  }
  const start = moment(from, 'YYYY-MM-DD', true);
  const end = moment(to, 'YYYY-MM-DD', true);
  if (!start.isValid() || !end.isValid()) {
    return 'VALIDATION.DATE_INVALID';
  }
  if (start.isAfter(end)) {
    return 'ORDER_OVERSIGHT.VALIDATION.ORDER';
  }
  if (start.clone().add(1, 'year').isBefore(end)) {
    return 'ORDER_OVERSIGHT.VALIDATION.YEAR';
  }
  return '';
};

// DRAFT: wording to be approved by Małgorzata Gołębiewska with Grant
// Thornton (change request of 26.10, P3). Create and Bulk issue open with
// this text in Conditions; it is the same in both UI languages. The issuer
// replaces every [placeholder] before issuing.
export const ORDER_CONDITIONS_TEMPLATE = [
  'Zasady współpracy przy obsłudze klienta [nazwa klienta] w okresie obowiązywania zlecenia:',
  '– dostępność: [dni i godziny dostępności];',
  '– spotkania z klientem: [np. cotygodniowe spotkania zespołu];',
  '– terminy: zadania przekazane przez klienta lub JIT realizuję w uzgodnionych terminach.',
  'Rezygnacja: mogę zrezygnować ze zlecenia, zgłaszając to JIT; obsługuję klienta do końca miesiąca następującego po miesiącu zgłoszenia.',
  'JIT nie gwarantuje określonej liczby zadań w okresie zlecenia. Zlecenie może zostać zakończone przed terminem, jeśli zmienią się ustalenia JIT z klientem.',
].join('\n');

// A [placeholder] left in the conditions; the backend refuses the same.
export const CONDITIONS_PLACEHOLDER_PATTERN = /\[[^\]\n]{1,80}\]/;
export const CONDITIONS_REQUIRED = 'ORDERS.VALIDATION.CONDITIONS_REQUIRED';
export const CONDITIONS_PLACEHOLDER =
  'ORDERS.VALIDATION.CONDITIONS_PLACEHOLDER';

export const hasConditionsPlaceholder = (conditions?: string | null) =>
  CONDITIONS_PLACEHOLDER_PATTERN.test(conditions || '');

// Conditions are required (change request of 26.10, P3). An extension left
// blank keeps the predecessor's conditions, which then must not be blank
// (fallback).
export const validateConditions = (
  conditions?: string | null,
  fallback?: string | null,
) => {
  const text = conditions?.trim();
  if (!text) {
    return fallback?.trim() ? '' : CONDITIONS_REQUIRED;
  }
  return hasConditionsPlaceholder(text) ? CONDITIONS_PLACEHOLDER : '';
};

// Bulk issue checks each consultant's effective conditions: their own when
// not blank, else the shared ones (contract amendment A2). The shared value
// therefore matters only when some row uses it, and for a CSV batch, which
// has no per-row conditions (rows empty).
export const validateBulkConditions = (
  shared: string,
  rows: Array<{ values?: BulkOrderRowValues }>,
) => {
  const effective = rows.length
    ? rows.map(({ values }) => values?.conditions?.trim() || shared)
    : [shared];
  if (effective.some(hasConditionsPlaceholder)) {
    return CONDITIONS_PLACEHOLDER;
  }
  if (effective.some((conditions) => !conditions.trim())) {
    return 'ORDERS.BULK.VALIDATION.CONDITIONS_REQUIRED';
  }
  return '';
};

export const validateBulkRenewal = (
  request: Partial<BulkOrderRenewRequest>,
  selected: OrderSummary[],
  today: Moment = moment().startOf('day'),
) => {
  if (
    !request.clientId ||
    !request.startDate ||
    !request.endDate ||
    !request.decisionDeadline ||
    !selected.length
  ) {
    return 'ORDERS.BULK.VALIDATION.REQUIRED';
  }
  if (
    [request.startDate, request.endDate, request.decisionDeadline].some(
      isInvalidDate,
    )
  ) {
    return 'ORDERS.VALIDATION.DATES';
  }
  const codeError = validateOrderCode(request.orderCode);
  if (codeError) {
    return codeError;
  }
  // Blank conditions keep each predecessor's; typed ones must be complete.
  if (hasConditionsPlaceholder(request.conditions)) {
    return CONDITIONS_PLACEHOLDER;
  }
  if (moment(request.startDate).isAfter(request.endDate)) {
    return 'ORDERS.VALIDATION.END_BEFORE_START';
  }
  const start = moment(request.startDate).startOf('day');
  if (
    moment(request.decisionDeadline).isAfter(
      getLatestDecisionDeadline(start, today),
      'day',
    )
  ) {
    return deadlineAfterStartError(start, today);
  }
  if (moment(request.decisionDeadline).isBefore(today, 'day')) {
    return 'ORDERS.VALIDATION.DEADLINE_PAST';
  }
  if (start.isBefore(getEarliestStartDate(today), 'day')) {
    return 'ORDERS.VALIDATION.START_CLOSED_MONTH';
  }
  const invalidPredecessor = selected.some((order) =>
    moment(request.startDate).isSameOrBefore(
      order.effectiveEndDate || order.endDate,
      'day',
    ),
  );
  return invalidPredecessor ? 'ORDERS.VALIDATION.RENEWAL_START' : '';
};

export interface BulkConsultantRow {
  accountId: number;
  fullName: string;
  email?: string;
  contractType?: string;
  awaitingFirstSignIn?: boolean;
  // Corrections for this consultant only; blank values use the shared ones.
  values?: BulkOrderRowValues;
}

const ROW_VALUE_KEYS: Array<keyof BulkOrderRowValues> = [
  'startDate',
  'endDate',
  'decisionDeadline',
  'subject',
  'conditions',
];

// Per-row values for the bulk request, in consultant order, or null when no
// row is corrected (the shared values then apply to everyone).
export const toBulkOrderRows = (
  rows: BulkConsultantRow[],
): BulkOrderRow[] | null => {
  const requestRows = rows.map(({ accountId, values }) =>
    ROW_VALUE_KEYS.reduce<BulkOrderRow>(
      (row, key) => {
        const value = values?.[key]?.trim();
        return value ? { ...row, [key]: value } : row;
      },
      { consultantId: accountId },
    ),
  );
  return requestRows.some((row) => Object.keys(row).length > 1)
    ? requestRows
    : null;
};

// Appends consultants in order, ignoring anyone already in the batch.
export const addBulkConsultants = (
  current: BulkConsultantRow[],
  additions: BulkConsultantRow[],
) =>
  additions.reduce(
    (rows, addition) =>
      rows.some(({ accountId }) => accountId === addition.accountId)
        ? rows
        : [...rows, addition],
    current,
  );

export const csvCell = (value: string | number | null | undefined) =>
  `"${String(value ?? '').replace(/"/g, '""')}"`;

export const bulkErrorsCsv = (errors: BulkOrderError[]) =>
  [
    ['row', 'email', 'code', 'message'],
    ...errors.map(({ row, email, code, message }) => [
      row,
      email,
      code,
      message,
    ]),
  ]
    .map((row) => row.map(csvCell).join(','))
    .join('\r\n');

export interface OrderFormValues {
  consultant?: SelectOption | null;
  client?: SelectOption | null;
  owner?: SelectOption | null;
  startDate?: MomentInput | null;
  endDate?: MomentInput | null;
  decisionDeadline?: MomentInput | null;
  subject?: string;
  conditions?: string;
  orderCode?: string;
  notifyAlso?: SelectOption[] | null;
}

export type OrderFormErrors = Partial<Record<keyof OrderFormValues, string>>;

export const MAX_REASON_LENGTH = 10000;
export const MAX_SUBJECT_LENGTH = 500;

// The subject and the number prefix are optional additions; conditions are
// checked by validateConditions.
const requiredFields: Array<keyof OrderFormValues> = [
  'consultant',
  'client',
  'owner',
  'startDate',
  'endDate',
  'decisionDeadline',
];

const getRequiredErrors = (values: OrderFormValues): OrderFormErrors =>
  requiredFields.reduce<OrderFormErrors>((errors, field) => {
    const value = values[field];
    if (!value || (typeof value === 'string' && !value.trim())) {
      errors[field] = 'VALIDATION.REQUIRED';
    }
    return errors;
  }, {});

const parseDate = (
  value: MomentInput | null | undefined,
  field: keyof OrderFormValues,
  errors: OrderFormErrors,
) => {
  if (!value) {
    return null;
  }
  const date = moment(value).startOf('day');
  if (!date.isValid()) {
    errors[field] = 'VALIDATION.DATE_INVALID';
    return null;
  }
  return date;
};

// conditionsFallback: the predecessor's conditions an extension keeps when
// its own are left blank.
export const validateOrderForm = (
  values: OrderFormValues,
  today: Moment = moment().startOf('day'),
  conditionsFallback?: string | null,
): OrderFormErrors => {
  const errors = getRequiredErrors(values);
  const start = parseDate(values.startDate, 'startDate', errors);
  const end = parseDate(values.endDate, 'endDate', errors);
  const deadline = parseDate(
    values.decisionDeadline,
    'decisionDeadline',
    errors,
  );

  if (end && start?.isAfter(end)) {
    errors.endDate = 'ORDERS.VALIDATION.END_BEFORE_START';
  }
  if (start && deadline?.isAfter(getLatestDecisionDeadline(start, today))) {
    errors.decisionDeadline = deadlineAfterStartError(start, today);
  }
  if (deadline?.isBefore(today)) {
    errors.decisionDeadline = 'ORDERS.VALIDATION.DEADLINE_PAST';
  }
  if (start?.isBefore(getEarliestStartDate(today))) {
    errors.startDate = 'ORDERS.VALIDATION.START_CLOSED_MONTH';
  }
  const codeError = validateOrderCode(values.orderCode);
  if (codeError) {
    errors.orderCode = codeError;
  }
  if ((values.subject || '').trim().length > MAX_SUBJECT_LENGTH) {
    errors.subject = 'ORDERS.VALIDATION.SUBJECT_TOO_LONG';
  }
  const conditionsError = validateConditions(
    values.conditions,
    conditionsFallback,
  );
  if (conditionsError) {
    errors.conditions = conditionsError;
  }

  return errors;
};

// Blank optional text is sent as null so the backend stores nothing.
export const optionalText = (value?: string | null) => value?.trim() || null;

export const toNotifyAccountIds = (options?: SelectOption[] | null) =>
  Array.from(new Set((options || []).map(({ value }) => Number(value))));

export const toOrderCreateRequest = (
  values: OrderFormValues,
): OrderCreateRequest => ({
  consultantId: Number(values.consultant!.value),
  clientId: Number(values.client!.value),
  ownerId: Number(values.owner!.value),
  startDate: moment(values.startDate).format('YYYY-MM-DD'),
  endDate: moment(values.endDate).format('YYYY-MM-DD'),
  decisionDeadline: moment(values.decisionDeadline).format('YYYY-MM-DD'),
  subject: optionalText(values.subject),
  conditions: optionalText(values.conditions),
  orderCode: toOrderCode(values.orderCode),
  notifyAccountIds: toNotifyAccountIds(values.notifyAlso),
});

export const validateRenewalForm = (
  values: OrderFormValues,
  predecessor: OrderSummary,
  today: Moment = moment().startOf('day'),
  predecessorConditions?: string | null,
): OrderFormErrors => {
  const errors = validateOrderForm(values, today, predecessorConditions);
  const start = values.startDate
    ? moment(values.startDate).startOf('day')
    : null;
  const predecessorEnd = moment(
    predecessor.effectiveEndDate || predecessor.endDate,
  ).startOf('day');

  if (start?.isValid() && !start.isAfter(predecessorEnd)) {
    errors.startDate = 'ORDERS.VALIDATION.RENEWAL_START';
  }
  // A renewal keeps the consultant and the client (INM-126).
  if (
    values.consultant &&
    Number(values.consultant.value) !== predecessor.consultantId
  ) {
    errors.consultant = 'ORDERS.VALIDATION.RENEWAL_IDENTITY';
  }
  if (values.client && Number(values.client.value) !== predecessor.clientId) {
    errors.client = 'ORDERS.VALIDATION.RENEWAL_IDENTITY';
  }
  return errors;
};

type OrderPeriod = Pick<
  OrderSummary,
  'startDate' | 'endDate' | 'effectiveEndDate'
>;

const isWholeMonthPeriod = (start: Moment, end: Moment) =>
  start.date() === 1 && end.isSame(end.clone().endOf('month'), 'day');

// The previous month stays open until its settlement on this day of the next
// month (MONTHLY_SETTLEMENT_DAY of the backend).
const MONTHLY_SETTLEMENT_DAY = 5;

// Assignments may start on the 1st of the first month not yet closed: until
// the settlement day, the previous month (change request of 30.10).
export const getEarliestStartDate = (today: Moment = moment()) =>
  (today.date() > MONTHLY_SETTLEMENT_DAY
    ? today.clone()
    : today.clone().subtract(1, 'month')
  ).startOf('month');

// The latest decision deadline: the start, or for a start already past, the
// last day its month is still open, so the consultant decides before the
// month is settled.
export const getLatestDecisionDeadline = (
  startDate: MomentInput,
  today: Moment = moment().startOf('day'),
) => {
  const start = moment(startDate).startOf('day');
  return start.isBefore(today, 'day')
    ? start.clone().add(1, 'month').date(MONTHLY_SETTLEMENT_DAY)
    : start;
};

// The error of a deadline after the latest one: a past start has its own.
const deadlineAfterStartError = (start: Moment, today: Moment) =>
  start.isBefore(today, 'day')
    ? 'ORDERS.VALIDATION.DEADLINE_AFTER_OPEN_MONTH'
    : 'ORDERS.VALIDATION.DEADLINE_AFTER_START';

// The decision is due the day before the start, but never before today; the
// forms move an untouched deadline with the start (change request of 24.10,
// B1).
export const getDefaultDecisionDeadline = (
  startDate: MomentInput,
  today: Moment = moment().startOf('day'),
) =>
  moment.max(
    moment(startDate).startOf('day').subtract(1, 'day'),
    today.clone().startOf('day'),
  );

// The default deadline (YYYY-MM-DD) for a start typed into a date field; an
// empty or invalid start keeps the current deadline.
export const followStartDate = (start: string, currentDeadline: string) => {
  const date = moment(start, 'YYYY-MM-DD', true);
  return start && !isInvalidDate(start) && date.isValid()
    ? getDefaultDecisionDeadline(date).format('YYYY-MM-DD')
    : currentDeadline;
};

// A bulk row's own values after its start changes: unless the issuer edited
// the row's deadline, it follows a usable new start, so a backdated row is
// not left with the shared deadline. A half-typed start leaves it as it is.
export const withRowStartDate = (
  values: BulkOrderRowValues | undefined,
  startDate: string,
  deadlineTouched: boolean,
): BulkOrderRowValues => {
  const deadline = followStartDate(startDate, '');
  return !deadlineTouched && deadline
    ? { ...values, startDate, decisionDeadline: deadline }
    : { ...values, startDate };
};

// The latest decision deadline (YYYY-MM-DD) for a typed start, if it is a
// usable date.
export const getDeadlineMax = (
  start: string,
  today: Moment = moment().startOf('day'),
) =>
  start && !isInvalidDate(start) && moment(start, 'YYYY-MM-DD', true).isValid()
    ? getLatestDecisionDeadline(start, today).format('YYYY-MM-DD')
    : undefined;

// The successor starts the day after the predecessor's effective end, or on
// the 1st of the first open month when that day is in a closed month, and
// keeps its length: whole calendar months stay whole months (01.05-31.10 ->
// 01.11-30.04), other periods keep their number of days. The decision is due
// the day before the start, never before today.
export const getSuccessorPeriod = (
  order: OrderPeriod,
  startDate?: Moment,
  today: Moment = moment().startOf('day'),
) => {
  const originalStart = moment(order.startDate).startOf('day');
  const originalEnd = moment(order.endDate).startOf('day');
  const start = moment.max(
    startDate?.clone().startOf('day') ||
      moment(order.effectiveEndDate || order.endDate)
        .startOf('day')
        .add(1, 'day'),
    getEarliestStartDate(today),
  );
  const months = originalEnd
    .clone()
    .add(1, 'day')
    .diff(originalStart, 'months', true);
  const end =
    isWholeMonthPeriod(originalStart, originalEnd) && Number.isInteger(months)
      ? start.clone().add(months, 'months').subtract(1, 'day')
      : start.clone().add(originalEnd.diff(originalStart, 'days'), 'days');
  return {
    startDate: start,
    endDate: end,
    decisionDeadline: getDefaultDecisionDeadline(start, today),
  };
};

// Bulk renewal shares one period: it starts after the latest selected
// effective end (so no successor overlaps its predecessor) and takes the
// first selected order's length.
export const getBulkRenewalPeriod = (
  selected: OrderPeriod[],
  today: Moment = moment().startOf('day'),
) => {
  if (!selected.length) {
    return null;
  }
  const latestEnd = moment.max(
    selected.map((order) =>
      moment(order.effectiveEndDate || order.endDate).startOf('day'),
    ),
  );
  return getSuccessorPeriod(
    selected[0],
    latestEnd.clone().add(1, 'day'),
    today,
  );
};

const RENEWABLE_STATUSES: OrderLifecycleStatus[] = [
  'ACCEPTED',
  'COMPLETED',
  'EARLY_ENDED',
];

// Rejected, cancelled and expired orders are replaced by a new order rather
// than renewed. The backend flag wins when present.
export const isOrderRenewable = (
  order: Pick<
    OrderSummary,
    'renewable' | 'successorOrderNumber' | 'lifecycleStatus'
  >,
) =>
  typeof order.renewable === 'boolean'
    ? order.renewable
    : !order.successorOrderNumber &&
      RENEWABLE_STATUSES.includes(order.lifecycleStatus);

export const getDaysUntil = (
  date: string,
  today: Moment = moment().startOf('day'),
) => moment(date).startOf('day').diff(today.clone().startOf('day'), 'days');

export interface OrderStatusNote {
  id: string;
  values?: Record<string, string | number>;
}

// Short hints under the status: time left for a pending decision and the
// order this one renews.
export const getOrderStatusNotes = (
  order: Pick<
    OrderSummary,
    'status' | 'decisionDeadline' | 'predecessorOrderNumber'
  >,
  today: Moment = moment().startOf('day'),
): OrderStatusNote[] => {
  const notes: OrderStatusNote[] = [];
  if (order.status === 'PENDING' && order.decisionDeadline) {
    const days = getDaysUntil(order.decisionDeadline, today);
    if (days >= 0) {
      notes.push({ id: 'ORDERS.NOTE.DAYS_LEFT', values: { count: days } });
    }
  }
  if (order.predecessorOrderNumber) {
    notes.push({
      id: 'ORDERS.NOTE.RENEWAL_OF',
      values: { number: order.predecessorOrderNumber },
    });
  }
  return notes;
};

// When the consultant accepted the order: the latest ACCEPTED event.
export const getAcceptedAt = (events?: OrderEvent[] | null) => {
  const accepted = (events || []).filter(
    ({ eventType }) => eventType === 'ACCEPTED',
  );
  if (!accepted.length) {
    return null;
  }
  return accepted.reduce((latest, current) =>
    moment(current.occurredAt).isAfter(latest.occurredAt) ? current : latest,
  ).occurredAt;
};

// The consultant's rejection (who, when, why): the latest REJECTED event.
export const getRejectionEvent = (events?: OrderEvent[] | null) =>
  (events || [])
    .filter(({ eventType }) => eventType === 'REJECTED')
    .reduce<OrderEvent | null>(
      (latest, current) =>
        !latest || moment(current.occurredAt).isAfter(latest.occurredAt)
          ? current
          : latest,
      null,
    );

// Employment-contract orders are issued and accepted at once, without a
// consultant decision, so they read "Issued" where B2B and civil-contract
// orders read "Accepted". ACCEPTED stays the status in the data.
export const isEmploymentOrder = (order: { contractType?: string | null }) =>
  order.contractType === 'EMPLOYMENT_CONTRACT';

export const getLifecycleStatusLabel = (
  status: OrderLifecycleStatus,
  contractType?: string | null,
) =>
  status === 'ACCEPTED' && contractType === 'EMPLOYMENT_CONTRACT'
    ? 'ORDERS.STATUS.ISSUED'
    : `ORDERS.STATUS.${status}`;

// Only the consultant accepts a B2B or civil-contract order; the automatic
// acceptance of an employment-contract order is recorded for its issuer.
// History rows carry no contract type, so the actor tells them apart.
export const isAutomaticAcceptance = (
  event: { eventType: string; actorId?: number | null },
  consultantId?: number,
) =>
  event.eventType === 'ACCEPTED' &&
  (!event.actorId || event.actorId !== consultantId);

export const getOrderEventLabel = (eventType: string, automatic: boolean) =>
  automatic ? 'ORDERS.EVENT.ISSUED_NO_DECISION' : `ORDERS.EVENT.${eventType}`;

export const toOrderRenewRequest = (
  values: OrderFormValues,
): OrderRenewRequest => ({
  ...toOrderCreateRequest(values),
});

// notifyCandidates: the people Notify also may name; a copied recipient who
// is no longer one of them is dropped (change request of 24.10, D2).
export const getRenewalFormValues = (
  details: OrderDetails,
  employees: SelectOption[],
  clients: SelectOption[],
  notifyCandidates: SelectOption[],
  today: Moment = moment().startOf('day'),
): OrderFormValues => {
  const period = getSuccessorPeriod(details.order, undefined, today);
  const notifyIds = (details.notifyAlso || []).map(({ id }) => id);

  return {
    // Consultant and client are locked on renewal, so an inactive client or
    // a consultant missing from the lists still keeps the predecessor's.
    consultant: employees.find(
      ({ value }) => value === details.order.consultantId,
    ) || {
      value: details.order.consultantId,
      name: details.order.consultantName || String(details.order.consultantId),
    },
    client: clients.find(({ value }) => value === details.order.clientId) || {
      value: details.order.clientId,
      name: details.order.clientName || String(details.order.clientId),
    },
    owner:
      employees.find(({ value }) => value === details.order.ownerId) || null,
    ...period,
    // A renewal keeps the predecessor's subject and conditions by default.
    subject: details.subject || '',
    conditions: details.conditions || '',
    orderCode: getOrderCodeFromNumber(details.order.orderNumber),
    notifyAlso: notifyCandidates.filter(({ value }) =>
      notifyIds.includes(Number(value)),
    ),
  };
};

// New orders usually start on the 1st of next month and need a decision by the
// day before; the owner can change both.
export const getDefaultIssueDates = (today: Moment = moment()) => {
  const startDate = today.clone().add(1, 'month').startOf('month');
  return {
    startDate,
    decisionDeadline: getDefaultDecisionDeadline(
      startDate,
      today.clone().startOf('day'),
    ),
  };
};

// Only pending orders are actionable: after the decision deadline the backend
// returns 409 and a new order is required. Employment-contract orders need no
// decision.
export const canConsultantAccept = (order: OrderSummary) =>
  order.contractType !== 'EMPLOYMENT_CONTRACT' &&
  order.lifecycleStatus === 'PENDING';

// B2B and civil-contract consultants decide on pending orders themselves;
// employment-contract orders need no decision and cannot be rejected.
export const canConsultantReject = (order: OrderSummary) =>
  (order.contractType === 'B2B' || order.contractType === 'CIVIL_CONTRACT') &&
  order.lifecycleStatus === 'PENDING';

export const isPastDecisionDeadline = (
  order: Pick<OrderSummary, 'lifecycleStatus'>,
) => order.lifecycleStatus === 'EXPIRED';

export const canManagerCancel = (
  order: OrderSummary,
  today: Moment = moment().startOf('day'),
) =>
  moment(order.startDate).startOf('day').isAfter(today) &&
  ['PENDING', 'EXPIRED', 'ACCEPTED'].includes(order.lifecycleStatus);

export const canManagerEndEarly = (
  order: Pick<OrderSummary, 'lifecycleStatus' | 'status'>,
) => order.lifecycleStatus === 'ACCEPTED' && order.status === 'ACTIVE';

export const validateRequiredReason = (reason: string) => {
  if (!reason.trim()) {
    return 'VALIDATION.REQUIRED';
  }
  if (reason.trim().length > MAX_REASON_LENGTH) {
    return 'ORDERS.VALIDATION.REASON_TOO_LONG';
  }
  return '';
};

// The assignment a create or extend overlaps (same consultant, client and
// period): the backend answers 409 with code ORDER_OVERLAP. Every other
// conflict (ORDER_CHANGED, or no code) is stale data and asks for a reload.
export const ORDER_OVERLAP = 'ORDER_OVERLAP';
export const getOverlappingOrderNumber = (result: {
  status: number;
  code?: string;
  orderNumber?: string;
}) =>
  result.status === 409 && result.code === ORDER_OVERLAP
    ? result.orderNumber || ''
    : null;

// Reasons to cancel or end an assignment early (owner, 24.10, D9). The
// stored reason is the English label, or "Other: " with the typed text, so
// it reads the same in every language and next to older free-text reasons.
export const ORDER_END_REASONS = [
  { id: 'CLIENT_BUDGET', text: 'Client: end of budget or project' },
  { id: 'CLIENT_REPLACED', text: 'Client: consultant replaced' },
  { id: 'CONSULTANT_NOTICE', text: 'Consultant: notice or resignation' },
  { id: 'CONSULTANT_MOVED', text: 'Consultant: moved to another assignment' },
  { id: 'MISTAKE', text: 'Issued by mistake' },
  { id: 'OTHER', text: 'Other' },
] as const;
export type OrderEndReason = typeof ORDER_END_REASONS[number]['id'];
const OTHER_REASON_PREFIX = 'Other: ';

export const toOrderEndReason = (reason: OrderEndReason, other = '') =>
  reason === 'OTHER'
    ? `${OTHER_REASON_PREFIX}${other.trim()}`
    : ORDER_END_REASONS.find(({ id }) => id === reason)!.text;

// Errors of the reason select and of the text "Other" requires.
export const validateOrderEndReason = (
  reason: OrderEndReason | '',
  other = '',
): { reason?: string; other?: string } => {
  if (!reason) {
    return { reason: 'VALIDATION.REQUIRED' };
  }
  if (reason !== 'OTHER') {
    return {};
  }
  if (!other.trim()) {
    return { other: 'VALIDATION.REQUIRED' };
  }
  return other.trim().length + OTHER_REASON_PREFIX.length > MAX_REASON_LENGTH
    ? { other: 'ORDERS.VALIDATION.REASON_TOO_LONG' }
    : {};
};

export const getActionErrorDetail = (status: number, message?: string) =>
  (status === 400 || status === 409) && message ? message : '';

export const getActionErrorMessage = (status: number) => {
  if (status === 400) {
    return 'ORDERS.ACTION_ERROR.VALIDATION';
  }
  if (status === 401 || status === 403) {
    return 'ORDERS.ACTION_ERROR.UNAUTHORIZED';
  }
  if (status === 409) {
    return 'ORDERS.ACTION_ERROR.CONFLICT';
  }
  return 'ORDERS.ACTION_ERROR.SERVER';
};

export const RENEWAL_WINDOW_DAYS = 30;

// Number of accepted orders ending within the renewal window without a
// successor. The first page is checked against the rule so an older backend
// that ignores the filters does not produce a misleading reminder.
export const getRenewalReminderCount = (
  page: { content: OrderSummary[]; totalElements: number },
  today: Moment = moment().startOf('day'),
) => {
  const matches = page.content.every((order) => {
    const daysLeft = getDaysUntil(
      order.effectiveEndDate || order.endDate,
      today,
    );
    return (
      order.lifecycleStatus === 'ACCEPTED' &&
      !order.successorOrderNumber &&
      daysLeft >= 0 &&
      daysLeft <= RENEWAL_WINDOW_DAYS
    );
  });
  return matches ? page.totalElements : 0;
};

// Names of the active projects of the client the user is assigned to: the
// projects an accepted order lets them report time on.
export const getClientProjectNames = (
  projects: AssignedProject[],
  clientId: number,
) =>
  projects
    .filter(
      ({ client, status }) =>
        client?.id === clientId && (!status || status === 'ACTIVE'),
    )
    .map(({ projectName }) => projectName)
    .sort((a, b) => a.localeCompare(b));

// A consultant the Entra account sync created may get an assignment before
// signing in for the first time (B7); the name says so wherever they are
// picked or listed.
export const consultantName = (
  intl: IntlShape,
  name: string,
  awaitingFirstSignIn?: boolean,
) =>
  awaitingFirstSignIn
    ? intl.formatMessage({ id: 'ORDERS.NOT_SIGNED_IN_NAME' }, { name })
    : name;
